/**
 * user_items resource — pure async data functions.
 *
 * Hooks/components must not call `supabase.from('user_items')` directly.
 * Add new reads/writes here and consume them from a hook in `src/hooks/`
 * or a page. Read hooks that already own their own `select(...)` shape
 * (e.g. `useMyItems`, `useInventoryWarmup`) may keep calling supabase
 * directly for now — this module targets mutation surfaces first.
 */
import { supabase } from '@/integrations/supabase/client';
import type { UserItem } from '@/types/userItem';

export type UserItemUpdate = Partial<Record<string, unknown>>;
export type UserItemInsert = Record<string, unknown>;

/** Update a single user_items row by id. Throws on error. */
export async function updateUserItem(
  itemId: string,
  patch: UserItemUpdate,
): Promise<void> {
  const { error } = await supabase
    .from('user_items')
    .update(patch as never)
    .eq('id', itemId);
  if (error) throw error;
}

/** Delete a single user_items row. Throws on error. */
export async function deleteUserItem(itemId: string): Promise<void> {
  const { error } = await supabase.from('user_items').delete().eq('id', itemId);
  if (error) throw error;
}

/** Bulk delete user_items rows by id list. Throws on error. */
export async function deleteUserItems(itemIds: string[]): Promise<void> {
  if (itemIds.length === 0) return;
  const { error } = await supabase.from('user_items').delete().in('id', itemIds);
  if (error) throw error;
}

/** Insert a single user_items row. Returns nothing; throws on error. */
export async function insertUserItem(payload: UserItemInsert): Promise<void> {
  const { error } = await supabase.from('user_items').insert(payload as never);
  if (error) throw error;
}

/**
 * Duplicate a source item as a private draft, copying identity-only fields.
 * Resets price, cost, proof, sale, and eBay-related state.
 */
export async function duplicateUserItemAsDraft(
  source: UserItem,
  ownerId: string,
): Promise<void> {
  const s = source as UserItem & Record<string, unknown>;
  const payload: UserItemInsert = {
    user_id: ownerId,
    title: source.title?.includes('(Duplicate)')
      ? source.title
      : `${source.title} (Duplicate)`,
    category: source.category,
    game: s.game,
    custom_game_name: s.custom_game_name,
    language: source.language,
    set_code: source.set_code,
    set_name: source.set_name,
    era: s.era,
    product_type: s.product_type,
    card_number: source.card_number,
    finish: s.finish,
    finish_label: s.finish_label,
    rarity: s.rarity,
    condition: source.condition,
    quantity: 1,
    visibility: 'private',
    is_listed: false,
    is_negotiable: false,
    accepts_trades: false,
    stock_image_url: s.stock_image_url,
    slab_grade: source.category === 'slabs' ? s.slab_grade : null,
    grading_company: source.category === 'slabs' ? s.grading_company : null,
  };
  await insertUserItem(payload);
}

/**
 * Finalize a Pending eBay Sale: stamps sold_at + sold_price, decrements
 * quantity by the pending amount (FIFO drops cost basis), and clears the
 * pending flags. If stock hits zero the item stays private (sold out).
 */
export async function confirmPendingSale(item: UserItem): Promise<void> {
  const s = item as UserItem & Record<string, any>;
  const qty = Math.max(1, Number(s.pending_sale_quantity ?? 1));
  const remaining = Math.max(0, Number(s.quantity ?? 0) - qty);
  const saleTotalCents = s.pending_sale_price_cents != null
    ? Number(s.pending_sale_price_cents)
    : null;
  const saleAt = new Date().toISOString();

  let updatedCosts: number[] | null = null;
  if (Array.isArray(s.individual_costs_cents)) {
    updatedCosts = (s.individual_costs_cents as number[]).slice(qty);
  }

  const patch: UserItemUpdate = {
    quantity: remaining,
    sold_at: remaining === 0 ? saleAt : null,
    sold_price_cents: remaining === 0 ? saleTotalCents : null,
    is_listed: remaining > 0 && s.pending_prior_visibility === 'listed',
    visibility: remaining > 0
      ? ((s.pending_prior_visibility as string | null) ?? 'private')
      : 'private',
    pending_sale_source: null,
    pending_sale_at: null,
    pending_sale_order_ref: null,
    pending_sale_price_cents: null,
    pending_sale_quantity: null,
    pending_prior_visibility: null,
  };
  if (updatedCosts) patch.individual_costs_cents = updatedCosts.length > 0 ? updatedCosts : null;
  await updateUserItem(item.id, patch);
}

/**
 * Restore a Pending eBay Sale back into inventory (e.g. return / cancel).
 * Quantity was never decremented, so we just clear the pending flags and
 * put visibility back to what it was before the sale synced in.
 */
export async function restorePendingSale(item: UserItem): Promise<void> {
  const prior = ((item as any).pending_prior_visibility as string | null) ?? 'listed';
  const patch: UserItemUpdate = {
    visibility: prior,
    is_listed: prior === 'listed',
    pending_sale_source: null,
    pending_sale_at: null,
    pending_sale_order_ref: null,
    pending_sale_price_cents: null,
    pending_sale_quantity: null,
    pending_prior_visibility: null,
  };
  await updateUserItem(item.id, patch);
}
